import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import FormControls from '../formControls';
import { Row, Col, Form, Button, Modal, Tabs, Tab } from 'react-bootstrap';
import { passwordRegexString } from '../../utilities/authConstants';
import userService from '../../services/userService';
import LoadingButton from '../buttons/LoadingButton';
import { LICENSE_TYPE, ROLE_IDS } from '../../utilities/constants';

export const TAB_KEYS = Object.freeze({
    CREATE: "create",
    IMPORT: "import"
})

const tabStyle = {
    paddingInline: '8px'
}

class CreateNewUserModal extends Component {
    constructor(props, context) {
        super(props, context);
        this.state = {
            user: {
                organizationId: 0,
                userType: 0,
                firstName: "",
                lastName: "",
                emailAddress: "",
                password: "",
                confirmPassword: "",
                roleIds: []
            },
            roles: [],
            selectedTabKey: TAB_KEYS.CREATE,
            potentialSpmWebUsers: [],
            rolesApplicableForUserImport: [],
            potentialSpmWebUserSearchValue: "",
            filteredPotentialSpmWebUsers: []
        };
    }

    componentDidMount() {
        const user = this.state.user;
        if (this.props.model.organizationTypeId !== 4) {
            // if it isn't a steris org then normal user type
            user.userType = 1;
        } else {
            // if it is steris - then steris user type
            user.userType = 3;
        }
        user.organizationId = this.props.model.id;

        this.setState(
            { 
                user: user, 
            }, () => {
                this.getRoleTypes(); 
            });
        
    }

    getRoleTypes() {
        userService.getRoles(this.state.user.userType, this.state.user.organizationId)
            .then(response => {
                if (response.data.success) {
                    const rolesApplicableForUserImport = [ROLE_IDS.PAM_ADMIN];
                    const result = response.data.payload.filter(r => r.id !== ROLE_IDS.PAM_ADMIN && r.id !== ROLE_IDS.PAM_USER);
                    
                    let userImportRoles = response.data.payload?.filter(r => rolesApplicableForUserImport.includes(r.id));
                    userImportRoles = this.removePamAdminRoleIfOrgHasNoPamSiteLicense(userImportRoles);
                    this.setState({ roles: result, rolesApplicableForUserImport: userImportRoles });
                }
            });
    }

    removePamAdminRoleIfOrgHasNoPamSiteLicense(roles) {
        if (!this.props.model.licenses.find(l => l.licenseTypeId === LICENSE_TYPE.PAMSiteLicense)) {
            return roles.filter(r => r.id !== ROLE_IDS.PAM_ADMIN);
        } else {
            return roles;
        }
    }

    getOrgSiteFromForeignSiteId(foreignSiteId) {
        return this.props.model.sites.find(s => s.foreignSiteId === foreignSiteId);
    }

    getPotentialSpmWebUsers() {
        if (this.props.model.fullyQualifiedDomainName) {
            userService.getPotentialSpmWebUsers(this.props.orgId)
                .then(response => {
                    if (response.data.success) {
                        // this is only used for PAM currently, so for now we're filtering out any users who have an orgSiteId that does not have a PAM Site License
                        const result = response.data.payload;

                        const usersInSiteThatHavePAMSiteLicense = result
                        .filter(u => u.siteId && 
                            this.props.model.licenses.find(l => l.licenseTypeId === LICENSE_TYPE.PAMSiteLicense && l.orgSiteId === this.getOrgSiteFromForeignSiteId(u.siteId)?.id
                        ))
                        .sort((a, b) => a.firstName.localeCompare(b.firstName));
                        
                        this.setState({ potentialSpmWebUsers: usersInSiteThatHavePAMSiteLicense });
                        this.setState({ filteredPotentialSpmWebUsers: usersInSiteThatHavePAMSiteLicense });
                    }
                });
        }
    }

    filterPotentialSpmWebUsers(searchValue) {
        this.setState(prev => ({ filteredPotentialSpmWebUsers: prev.potentialSpmWebUsers.filter(user => {
            return  user.firstName.toLowerCase().includes(searchValue.toLowerCase()) || 
                    user.lastName.toLowerCase().includes(searchValue.toLowerCase()) || 
                    user.name.toLowerCase().includes(searchValue.toLowerCase());
        })}));
    }

    render() {
        const t = this.props.t;
        const initialValues = { ...this.state.user };
        
        const createNewUserSchema = Yup.object().shape({
            firstName: Yup.string()
                .required(t("First name is required.")),
            lastName: Yup.string()
                .required(t("Last name is required.")),
            emailAddress: Yup.string()
                .email(t("Invalid email address format"))
                .required(t("Email address is required")),
            password: Yup
                .string()
                .required(t("Please enter a password."))
                .matches(
                    new RegExp(passwordRegexString),
                    t("Requires at least 8 characters including: uppercase, lowercase, number, and at least one special character @$!%*?&")
                ),
            confirmPassword: Yup
                .string()
                .required(t("Confirmation of password is required."))
                .oneOf([Yup.ref("password"), null], t("Passwords must match")),

            roleIds: Yup.array().min(1, t("A web security role must be selected for the user."))
        })

        const importSpmLocalUserSchema = Yup.object().shape({
            spmLocalUserIds: Yup.array().min(1, t("A user must be selected to import.")),
            roleIds: Yup.array().min(1, t("A role must be selected for the user."))
        })

        const rolesToDisplay = this.state.selectedTabKey === TAB_KEYS.CREATE ? this.state.roles : this.state.rolesApplicableForUserImport;

        return (
            <Formik
                initialValues={initialValues}
                validationSchema={this.state.selectedTabKey === TAB_KEYS.CREATE ? createNewUserSchema : importSpmLocalUserSchema}
                enableReinitialize={true}
                onSubmit={(values, actions) => {
                    const tmpValues = { ...values };
                    tmpValues.roleIds = values.roleIds.map(v => parseInt(v));

                    if (this.state.selectedTabKey === TAB_KEYS.IMPORT) {
                        tmpValues.spmLocalUserIds = values.spmLocalUserIds.map(v => parseInt(v));
                        this.props.importSpmLocalUsers(this.props.model.id, tmpValues.spmLocalUserIds, tmpValues.roleIds);
                    } else {
                        this.props.saveModel(tmpValues);
                    }
                    actions.setSubmitting(false);

                }}>
                {
                    formik => 
                        (
                            <Form onSubmit={formik.handleSubmit}>
                                <Modal.Header>
                                    <Modal.Title>{t("Create New User")}</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <Row>
                                        <Col lg={12}>
                                            <Form.Group>
                                                <Form.Label>{t("Web Security Roles")}</Form.Label>
                                                {
                                                    rolesToDisplay.map((item, i) => {
                                                        return (
                                                            <FormControls.Checkbox key={i}
                                                                id={"roleIdCheck" + i}
                                                                label={item.name}
                                                                name="roleIds"
                                                                feedback=""
                                                                value={item.id}
                                                                formHandler={formik} />
                                                        )
                                                    })
                                                }
                                                <FormControls.Feedback 
                                                    name="roleIds"
                                                    formHandler={formik} />
                                            </Form.Group>

                                            <Tabs activeKey={this.state.selectedTabKey} onSelect={key => this.setState(current =>
                                                { 
                                                    if (key === TAB_KEYS.IMPORT) {
                                                        this.getPotentialSpmWebUsers();
                                                    }
                                                    return {...current, selectedTabKey: key}
                                                }, )}>
                                            
                                                <Tab eventKey={TAB_KEYS.CREATE} title={t("Create New User")} style={tabStyle}>
                                                    <Form.Group>
                                                        <FormControls.Textbox
                                                            label={t("First Name")}
                                                            name="firstName"
                                                            formHandler={formik} />
                                                    </Form.Group>
                                                    <Form.Group>
                                                        <FormControls.Textbox
                                                            label={t("Last Name")}
                                                            name="lastName"
                                                            formHandler={formik} />
                                                    </Form.Group>
                                                    <Form.Group>
                                                        <FormControls.Textbox
                                                            type="email"
                                                            label={t("Email Address")}
                                                            name="emailAddress"
                                                            formHandler={formik} />
                                                    </Form.Group>
                                                    <Form.Group>
                                                        <FormControls.Textbox
                                                            type="password"
                                                            label={t("Password")}
                                                            name="password"
                                                            formHandler={formik} />
                                                    </Form.Group>
                                                    <Form.Group>
                                                        <FormControls.Textbox
                                                            type="password"
                                                            label={t("Confirm Password")}
                                                            name="confirmPassword"
                                                            formHandler={formik} />
                                                    </Form.Group>
                                                </Tab>
                                                {this.state.rolesApplicableForUserImport.length > 0 && (
                                                    <Tab eventKey={TAB_KEYS.IMPORT} title={t("Import User")} style={{...tabStyle, maxHeight: '50vh', overflow: 'scroll'}}>
                                                        <div className="form-group" style={{paddingTop: '8px'}}>
                                                            <input type='text' className="form-control" id="importUserSearch" name="importUserSearch" placeholder="Search users" value={this.state.potentialSpmWebUserSearchValue} 
                                                                onChange={(e) => {
                                                                    this.setState({ potentialSpmWebUserSearchValue: e.target.value });
                                                                    this.filterPotentialSpmWebUsers(e.target.value);
                                                                }}
                                                            />
                                                        </div>
                                                        {!this.props.model.fullyQualifiedDomainName ? (
                                                            <p>Cannot retrieve users: FQDN not provided</p>
                                                        ) : (
                                                            <>
                                                                {this.state.potentialSpmWebUsers.length === 0 && (
                                                                    <p>No users to import</p>
                                                                )}

                                                                {this.state.filteredPotentialSpmWebUsers.length === 0 && this.state.potentialSpmWebUsers.length > 0 && (
                                                                    <p>No users found</p>
                                                                )}

                                                                {this.state.filteredPotentialSpmWebUsers?.map((user, i) => {
                                                                    return (
                                                                        <FormControls.Checkbox key={user.id}
                                                                            id={"potential-spm-web-user" + user.id}
                                                                            label={
                                                                                <>
                                                                                    <span style={{display:'block', fontWeight: 'bold'}}>{`${user.firstName} ${user.lastName}`} {this.getOrgSiteFromForeignSiteId(user.siteId) && `(${this.getOrgSiteFromForeignSiteId(user.siteId)?.name})`}</span>
                                                                                    <span style={{display:'block'}}>SPM username: {user.name}</span>
                                                                                </>
                                                                            }
                                                                            name="spmLocalUserIds"
                                                                            feedback=""
                                                                            value={user.id}
                                                                            formHandler={formik}
                                                                            style={{borderBottom: i !== this.state.filteredPotentialSpmWebUsers.length - 1 && '1px solid lightgrey', display:'flex', alignItems:'center'}}
                                                                        />
                                                                    )
                                                                })}
                                                            </>
                                                        )}
                                                    </Tab>
                                                )}
                                            </Tabs>
                                        </Col>
                                    </Row>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button disabled={formik.isSubmitting} onClick={() => this.props.closeModal()} variant="warning" type="button">{t("Cancel")}</Button>
                                    <LoadingButton
                                        loading={formik.isSubmitting}
                                        disabled={!formik.dirty}
                                        variant="primary"
                                        type="submit">{this.state.selectedTabKey === TAB_KEYS.CREATE ? t("Create") : t("Import")}
                                    </LoadingButton>
                                </Modal.Footer>
                            </Form>

                        )
                }
            </Formik>
        );
    }
}

export default withTranslation()(CreateNewUserModal);